import { createActionGroup, props, emptyProps } from '@ngrx/store';
import { Wallpaper } from '@era/core';
import { Device, DeviceLink, DeviceState, ModelNameFlags, SyncType } from '@era/locations-sdk';
import { ErrorModel } from '@era/app';
import { LocationSubset } from './location-subset';

// ============================================================================
// RESOLVER ACTIONS (Group - using createActionGroup)
// ============================================================================

export const DeviceResolverActions = createActionGroup({
  source: 'Device Resolver',
  events: {
    'Devices requested': props<{ brandId: string; locationId: string }>(),
    'Devices for state requested': props<{ state: DeviceState }>(),
  },
});

// ============================================================================
// API ACTIONS (Group - using createActionGroup)
// ============================================================================

export const DeviceApiActions = createActionGroup({
  source: 'Device API',
  events: {
    'Device loaded': props<{ devices: Device[] }>(),
    'Device loaded error': props<{ error: ErrorModel }>(),
    'Device unassigned': props<{ device: Device }>(),
    'Device unassigned error': props<{ error: ErrorModel }>(),
    'Device unavailable': props<{ device: Device }>(),
    'Device unavailable error': props<{ error: ErrorModel }>(),
    'Device assigned': props<{ device: Device }>(),
    'Device assigned error': props<{ error: ErrorModel }>(),
    'Device marked as spare': props<{ device: Device }>(),
    'Device marked as spare error': props<{ error: ErrorModel }>(),
    'Device marked as housekeeping': props<{ device: Device }>(),
    'Device marked as housekeeping error': props<{ error: ErrorModel }>(),
    'Device marked as staff': props<{ device: Device }>(),
    'Device marked as staff error': props<{ error: ErrorModel }>(),
    'Device marked as front desk': props<{ device: Device }>(),
    'Device marked as front desk error': props<{ error: ErrorModel }>(),
    'Device marked as bar': props<{ device: Device }>(),
    'Device marked as bar error': props<{ error: ErrorModel }>(),
    'Device marked as kiosk': props<{ device: Device }>(),
    'Device marked as kiosk error': props<{ error: ErrorModel }>(),
    'Sync retried': props<{ udid: string }>(),
    'Device sync retry error': props<{ error: ErrorModel }>(),
    'Given sync retried': props<{ udid: string; syncType: SyncType }>(),
    'Device given sync retry error': props<{ error: ErrorModel }>(),
    'Sync devices request successful': props<{ udids: string[]; syncTypes: SyncType[] }>(),
    'OS update retried': props<{ udid: string }>(),
    'Device os update retry error': props<{ error: ErrorModel }>(),
    'OS update continued': props<{ udid: string }>(),
    'Device os update continue error': props<{ error: ErrorModel }>(),
    'Device Restart successful': emptyProps(),
    'Failed to restart device': props<{ error: ErrorModel }>(),
    'Restart devices request successful': emptyProps(),
    'Restart devices error': props<{ error: ErrorModel }>(),
  },
});

// ============================================================================
// SIGNALR ACTIONS (Group - using createActionGroup)
// ============================================================================

export const DeviceSignalRHubActions = createActionGroup({
  source: 'Device SignalR Hub',
  events: {
    'Device changed': props<{ device: Device }>(),
    'Device removed': props<{ device: Device }>(),
  },
});

// ============================================================================
// VIEW ACTIONS (Group - using createActionGroup)
// ============================================================================

export const DeviceUnassignViewActions = createActionGroup({
  source: 'Device unassigned view',
  events: {
    'Device unassigned': props<{ deviceLink: DeviceLink }>(),
    'Device unavailable': props<{ deviceLink: DeviceLink }>(),
  },
});

export const DeviceAssignViewActions = createActionGroup({
  source: 'Device assigned view',
  events: {
    'Device assigned': props<{ deviceLink: DeviceLink; brandId: string; locationId: string; roomId: string }>(),
    'Device marked as spare': props<{ deviceLink: DeviceLink; brandId: string; locationId: string }>(),
  },
});

export const DeviceHousekeepingViewActions = createActionGroup({
  source: 'Device housekeeping view',
  events: {
    'Device marked as housekeeping': props<{ link: DeviceLink; brandId: string; locationId: string }>(),
  },
});

export const DeviceStaffViewActions = createActionGroup({
  source: 'Device staff view',
  events: {
    'Device marked as staff': props<{ link: DeviceLink; brandId: string; locationId: string }>(),
  },
});

export const DeviceFrontDeskViewActions = createActionGroup({
  source: 'Device front desk view',
  events: {
    'Device marked as front desk': props<{ link: DeviceLink; brandId: string; locationId: string }>(),
  },
});

export const DeviceBarViewActions = createActionGroup({
  source: 'Device bar view',
  events: {
    'Device marked as bar': props<{ link: DeviceLink; brandId: string; locationId: string }>(),
  },
});

export const DeviceKioskViewActions = createActionGroup({
  source: 'Device kiosk view',
  events: {
    'Device marked as kiosk': props<{ link: DeviceLink; brandId: string; locationId: string }>(),
  },
});

export const DeviceSyncStatusViewActions = createActionGroup({
  source: 'Device Sync Status view',
  events: {
    'Retry sync': props<{ udid: string; forceSync: boolean }>(),
    'Retry given sync': props<{ syncType: SyncType; forceSync: boolean }>(),
    'Retry os update': props<{ udid: string }>(),
    'Continue os update': props<{ udid: string }>(),
  },
});

export const DevicesSyncModalActions = createActionGroup({
  source: 'Devices Sync Modal',
  events: {
    'Sync devices requested': props<{ locationSubset: LocationSubset; syncTypes: SyncType[]; force: boolean }>(),
  },
});

export const LocationSettingsWallpaperViewActions = createActionGroup({
  source: 'Location Settings Wallpaper View',
  events: {
    'Wallpaper command sent': props<{
      wallpaper: Wallpaper;
      brandUniqueId?: string;
      locationUniqueId?: string;
      roomUniqueId?: string;
    }>(),
  },
});

export const RoomSettingsWallpaperViewActions = createActionGroup({
  source: 'Room Settings Wallpaper View',
  events: {
    'Wallpaper command sent': props<{
      wallpaper: Wallpaper;
      brandUniqueId?: string;
      locationUniqueId?: string;
      roomUniqueId?: string;
    }>(),
  },
});

export const BrandSettingsWallpaperViewActions = createActionGroup({
  source: 'Brand Settings Wallpaper View',
  events: {
    'Wallpaper command sent': props<{
      wallpaper: Wallpaper;
      brandUniqueId?: string;
      locationUniqueId?: string;
      roomUniqueId?: string;
    }>(),
  },
});

export const DeviceApiWallpaperActions = createActionGroup({
  source: 'Device API Wallpaper',
  events: {
    'Wallpaper successfully set': emptyProps(),
    'Wallpaper failed to set': props<{ error: ErrorModel }>(),
  },
});

export const DeviceDetailsContextMenuActions = createActionGroup({
  source: 'Device details context menu',
  events: {
    'Device Restart command sent': props<{ udid: string }>(),
  },
});

export const DeviceModalActions = createActionGroup({
  source: 'Device Modal',
  events: {
    'Reboot devices requested': props<{ locationSubset: LocationSubset; modelNames: Set<ModelNameFlags> }>(),
  },
});

export const LocationViewActions = createActionGroup({
  source: 'Location view',
  events: {
    'Export devices': emptyProps(),
  },
});

export const BrandViewActions = createActionGroup({
  source: 'Brand view',
  events: {
    'Export devices': emptyProps(),
  },
});

export const GatewayApiActions = createActionGroup({
  source: 'Gateway API',
  events: {
    'Export devices success': emptyProps(),
    'Export devices failed': props<{ error: ErrorModel }>(),
  },
});

export const DeviceActionsActions = createActionGroup({
  source: 'Device Actions',
  events: {
    'Set desired name': props<{ deviceId: string; newName: string }>(),
    'Set desired name failed': props<{ error: ErrorModel }>(),
    'Set desired name succeeded': props<{ device: Device }>(),
  },
});
